import { parseSelectedLayers } from './zipCodeParser';
import { featureCollectionToTurfPolygons } from './createTurfPolygons';

/**
 * Ladda ett district från API och förbered alla layers
 * @param {string} id - District ID
 * @param {Object} mapService - Map service instance
 * @param {Object} mapState - MapStateManager instance
 * @param {Function} fetchGeoJSON - Function to fetch GeoJSON from CDN
 * @param {Function} updateLoadingStates - Callback to update loading states
 * @returns {Object} District data
 */
export async function loadDistrict(id, mapService, mapState, fetchGeoJSON, updateLoadingStates) {
        // Hämta district från API
        const district = await mapService.fetchDistrict(id);
        
        // Parsa urvalslager
        const parsedLayers = parseSelectedLayers(district.layerSelection);
        const parsedLayersKeys = Object.keys(parsedLayers);
        
        // Samla alla selected areas från alla layers
        const allSelectedAreas = [];
        let firstLayerType = null;
        
        // Ladda alla layers från CDN
        for (let i = 0; i < parsedLayersKeys.length; i++) {
            const layerType = parsedLayersKeys[i];
            if (parsedLayers[layerType].length > 0) {
                // Sätt district type
                mapState.setDistrictTypes(layerType);
                
                // Spara första layer type för selection type
                if (!firstLayerType) {
                    firstLayerType = layerType;
                }
                
                // Ladda GeoJSON om det inte redan är laddat
                await preloadLayer(layerType, mapState, fetchGeoJSON, updateLoadingStates);
                
                // Hämta selected polygon features för denna layer
                const selectedAreas = getSelectedPolygonFeatures(
                    parsedLayers[layerType],
                    layerType,
                    mapState
                );
                
                // Lägg till areas från denna layer till samlingen (istället för att ersätta)
                allSelectedAreas.push(...selectedAreas);
            }
        }
        
        // Sätt alla selected areas på en gång (inkluderar alla urvalslager)
        if (allSelectedAreas.length > 0 && firstLayerType) {
            mapState.setSelectionType(firstLayerType);
            mapState.setSelectedAreas(allSelectedAreas, true, true);
        }
        
        return district;
}

/**
 * Förladda en layer (GeoJSON) om den inte redan är laddad
 * @param {string} type - Layer type (t.ex. 'zip1', 'zip3', etc.)
 * @param {Object} mapState - MapStateManager instance
 * @param {Function} fetchGeoJSON - Function to fetch GeoJSON
 * @param {Function} updateLoadingStates - Callback to update loading states
 */
export async function preloadLayer(type, mapState, fetchGeoJSON, updateLoadingStates) {
        updateLoadingStates(false, true);
        
        // Kontrollera om GeoJSON redan är laddat
        if (!mapState.hasLayerType(type)) {
            const featureCollection = await fetchGeoJSON(type);
            
            // Renderera GeoJSON på kartan
            mapState.styleManager.renderGeoJSON(featureCollection, mapState.getMap());
            
            // Spara turf polygons för beräkningar
            mapState.updateTurfPolygons({
                [type]: featureCollectionToTurfPolygons(featureCollection)
            });
        }
        
        updateLoadingStates(false, false);
}

/**
 * Hämta selected polygon features baserat på lista av feature IDs
 * @param {Array} listOfFeatureIds - Array av feature IDs
 * @param {string} type - Layer type
 * @param {Object} mapState - MapStateManager instance
 * @returns {Array} Array av GeoJSON features
 */
export function getSelectedPolygonFeatures(listOfFeatureIds, type, mapState) {
        const selectedFeatures = [];
        const turfPolygons = mapState.getTurfPolygons(type);
        
        if (!turfPolygons) {
            console.warn('DistrictLoader: No turf polygons found for type', type);
            return selectedFeatures;
        }
        
        listOfFeatureIds.forEach(id => {
            const data = turfPolygons.find(
                data => data.feature.properties.id === id
            );
            
            if (data) {
                selectedFeatures.push({
                    ...data.feature
                });
            } else {
                console.warn('DistrictLoader: No feature data found for', id);
            }
        });

        return selectedFeatures;
}

